const API_BASE = import.meta.env.VITE_API_URL || '';
const LEGACY_TOKEN_KEY = 'auth_token';
const UNAUTHENTICATED_EVENT = 'velko:unauthenticated';
let sessionCheckPromise: Promise<boolean> | null = null;
let csrfRefreshPromise: Promise<void> | null = null;

/**
 * The browser SPA authenticates exclusively with the shared Sanctum session.
 * Remove historical Bearer credentials before the first auth check on every
 * frontend origin (marketing/app/admin). Native clients have their own API
 * clients and are not affected by this migration.
 */
function clearLegacyAuthToken(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch {
    // Storage can be unavailable in hardened/private browser contexts.
  }
}

clearLegacyAuthToken();

/**
 * A 401 from an individual feature endpoint does not necessarily mean that
 * the browser session has expired (an endpoint can have a stricter guard or
 * temporarily reject the current account). Only the canonical auth check is
 * allowed to invalidate the global React auth state.
 */
async function hasBrowserSessionEnded(): Promise<boolean> {
  if (sessionCheckPromise) return sessionCheckPromise;

  sessionCheckPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/auth/check`, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return false;

      const payload = (await response.json()) as { authenticated?: boolean };
      return payload.authenticated === false;
    } catch {
      // A network failure cannot prove that the session ended. Keeping the
      // current page avoids turning a transient outage into a logout loop.
      return false;
    } finally {
      sessionCheckPromise = null;
    }
  })();

  return sessionCheckPromise;
}

/** Lazy import to avoid bundling Zustand into a server-only path at type-check time. */
async function notifyPaywall(
  code: string,
  details: unknown,
  message: string,
  shopId: string | null,
): Promise<void> {
  try {
    const { triggerPaywall } = await import('@/stores/paywallStore');
    if (shopId !== localStorage.getItem('current_shop_id')) return;
    const d = (details ?? {}) as Record<string, unknown>;
    if (code === 'SUBSCRIPTION_LIMIT') {
      triggerPaywall({
        reason: 'limit_reached',
        code,
        resource: typeof d.resource === 'string' ? d.resource : undefined,
        current: typeof d.current === 'number' ? d.current : undefined,
        limit: typeof d.limit === 'number' ? d.limit : undefined,
        message,
      });
    } else {
      triggerPaywall({
        reason: 'feature_locked',
        code,
        feature: typeof d.feature === 'string' ? d.feature : undefined,
        message,
      });
    }
  } catch {
    // Paywall surfacing is best-effort; the ApiError is still thrown to the caller.
  }
}

class ApiError extends Error {
  code: string;
  details?: unknown;
  status: number;
  headers?: Record<string, string>;

  constructor(
    message: string,
    code: string,
    status: number,
    details?: unknown,
    headers?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.headers = headers;
  }
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  meta?: Record<string, unknown>;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

interface ApiResult<T> {
  body: ApiResponse<T>;
  headers: Headers;
}

function emptySuccessResponse<T>(): ApiResponse<T> {
  return {
    success: true,
    data: null as T,
  };
}

function getCookie(name: string): string | undefined {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return undefined;
}

const REQUEST_TIMEOUT_MS = 20_000;

async function ensureCsrf(forceRefresh = false): Promise<void> {
  if (csrfRefreshPromise) return csrfRefreshPromise;
  if (!forceRefresh && getCookie('XSRF-TOKEN')) return;
  csrfRefreshPromise = refreshCsrf();
  try {
    await csrfRefreshPromise;
  } finally {
    csrfRefreshPromise = null;
  }
}

async function refreshCsrf(): Promise<void> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`${API_BASE}/sanctum/csrf-cookie`, {
      credentials: 'include',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new ApiError(
        'Impossible de renouveler la protection de session. Réessaie.',
        'CSRF_REFRESH_FAILED',
        response.status,
      );
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError(
        'La connexion est trop lente. Vérifie ton réseau et réessaie.',
        'NETWORK_TIMEOUT',
        0,
      );
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const { body } = await requestWithMeta<T>(endpoint, options);
  return body;
}

async function requestWithMeta<T>(
  endpoint: string,
  options: RequestInit = {},
  _retryCount = 0,
): Promise<ApiResult<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(options.headers as Record<string, string>),
  };

  const shopId = localStorage.getItem('current_shop_id');
  if (shopId) {
    headers['X-Shop-Id'] = shopId;
  }

  const xsrfToken = getCookie('XSRF-TOKEN');
  if (xsrfToken) {
    headers['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken);
  }

  const externalSignal = options.signal ?? null;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/v1${endpoint}`, {
      credentials: 'include',
      ...options,
      headers,
      signal: controller.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      const aborted = externalSignal?.aborted === true;
      if (aborted) {
        throw new ApiError('Request cancelled', 'REQUEST_CANCELLED', 0);
      }
      throw new ApiError(
        'La connexion est trop lente. Vérifie ton réseau et réessaie.',
        'NETWORK_TIMEOUT',
        0,
      );
    }
    if (err instanceof TypeError) {
      throw new ApiError(
        'Impossible de joindre le serveur. Vérifie ta connexion internet.',
        'NETWORK_OFFLINE',
        0,
      );
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  // Legacy query functions do not all forward AbortSignal. Their late responses
  // must not open a paywall, clear permissions or retry against the next shop.
  const assertCurrentShop = () => {
    if (shopId !== localStorage.getItem('current_shop_id')) {
      throw new ApiError('Request cancelled', 'REQUEST_CANCELLED', 0);
    }
  };
  assertCurrentShop();

  if (res.status === 401) {
    clearLegacyAuthToken();
    const sessionEnded = await hasBrowserSessionEnded();
    if (sessionEnded && typeof window !== 'undefined') {
      window.dispatchEvent(new Event(UNAUTHENTICATED_EVENT));
    }
    const payload = await res.json().catch(() => null);
    const message = payload?.error?.message || 'Unauthenticated';
    const code = payload?.error?.code || 'UNAUTHENTICATED';
    throw new ApiError(message, code, 401, payload?.error?.details);
  }

  if (res.status === 419 && _retryCount < 1) {
    // Laravel rejects CSRF before the controller runs: only this explicit
    // rejection can be retried, never a timeout or an ambiguous server error.
    await ensureCsrf(true);
    assertCurrentShop();
    return requestWithMeta(endpoint, options, _retryCount + 1);
  }

  if (!res.ok) {
    const errorHeaders = {
      'x-chat-limit': res.headers.get('X-Chat-Limit') ?? '',
      'x-chat-remaining': res.headers.get('X-Chat-Remaining') ?? '',
    };
    const payload = await res
      .json()
      .catch(() => ({ error: { code: 'REQUEST_FAILED', message: 'Request failed' } }));
    const code =
      typeof payload?.error?.code === 'string' ? payload.error.code : `HTTP_${res.status}`;
    const message =
      typeof payload?.error?.message === 'string' ? payload.error.message : `HTTP ${res.status}`;

    assertCurrentShop();

    // Surface a contextual paywall for plan-gated 403s instead of letting the
    // raw error propagate (the modal becomes the affordance).
    if (res.status === 403 && (code === 'FEATURE_NOT_AVAILABLE' || code === 'SUBSCRIPTION_LIMIT')) {
      void notifyPaywall(code, payload?.error?.details, message, shopId);
    }

    if (res.status === 403) window.dispatchEvent(new Event('permissions-changed'));
    throw new ApiError(message, code, res.status, payload?.error?.details, errorHeaders);
  }

  if (res.status === 204 || res.status === 205) {
    return { body: emptySuccessResponse<T>(), headers: res.headers };
  }

  let body: ApiResponse<T>;
  try {
    body = (await res.json()) as ApiResponse<T>;
  } catch {
    body = emptySuccessResponse<T>();
  }

  return { body, headers: res.headers };
}

export const api = {
  get: <T>(endpoint: string, options?: RequestInit) => request<T>(endpoint, options),
  getWithMeta: <T>(endpoint: string) => requestWithMeta<T>(endpoint),

  post: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),
  postWithMeta: <T>(endpoint: string, body?: unknown, options?: RequestInit) =>
    requestWithMeta<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      ...options,
    }),

  put: <T>(endpoint: string, body: unknown) =>
    request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  patch: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(endpoint: string, body?: unknown) =>
    request<T>(endpoint, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    }),

  download: async (endpoint: string, filename?: string) => {
    const headers: Record<string, string> = { Accept: 'text/csv' };
    const shopId = localStorage.getItem('current_shop_id');
    if (shopId) {
      headers['X-Shop-Id'] = shopId;
    }
    const res = await fetch(`${API_BASE}/api/v1${endpoint}`, {
      credentials: 'include',
      headers,
    });
    if (!res.ok) {
      throw new Error(`Download failed: HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition');
    const match = disposition?.match(/filename="?([^"]+)"?/);
    const name = filename || match?.[1] || 'export.csv';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  },

  upload: async <T>(endpoint: string, formData: FormData) => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const shopId = localStorage.getItem('current_shop_id');
    if (shopId) {
      headers['X-Shop-Id'] = shopId;
    }
    const xsrfToken = getCookie('XSRF-TOKEN');
    if (xsrfToken) {
      headers['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken);
    }
    const res = await fetch(`${API_BASE}/api/v1${endpoint}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: formData,
    });

    const payload = await res
      .json()
      .catch(() => ({ error: { code: 'REQUEST_FAILED', message: 'Request failed' } }));

    if (!res.ok || payload?.success === false) {
      const code =
        typeof payload?.error?.code === 'string' ? payload.error.code : `HTTP_${res.status}`;
      const message =
        typeof payload?.error?.message === 'string' ? payload.error.message : `HTTP ${res.status}`;

      if (
        res.status === 403 &&
        (code === 'FEATURE_NOT_AVAILABLE' || code === 'SUBSCRIPTION_LIMIT')
      ) {
        void notifyPaywall(code, payload?.error?.details, message, shopId);
      }

      throw new ApiError(message, code, res.status, payload?.error?.details);
    }

    return payload as ApiResponse<T>;
  },
};

export { clearLegacyAuthToken, ensureCsrf, UNAUTHENTICATED_EVENT };
export type { ApiResponse, PaginatedResponse };
export { ApiError };
